import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useEffectOnce, useQuery, useQueryParam, useViewport } from '@chilecompra/react-kit'
import { Button, Toast, Typography } from '@chilecompra/react-kit/components'
import routes from '../../config/settings/routes'
import { useFormRef } from './QuoteStepOne.hooks'
import BasicInformation from './QuoteStepOne.basicInformation'
import ClosingDate from './QuoteStepOne.closingDate'
import SecondCallAutomaticComponent from './QuoteStepOne.secondCallAutomatic'
import ContactInformation from './QuoteStepOne.contactInformation'
import DeliveryAddress from './QuoteStepOne.deliveryAddress'
import DeliveryTime from './QuoteStepOne.deliveryTime'
import EstimatedBudget from './QuoteStepOne.estimatedBudget'
import Errors from '../../components/Custom/Errors'
import { useConfig } from '../ConfigProvider/ConfigProvider.hook'
import { topPage } from '../../modules/utils/topPage'
import {
  onGetDistrictsThunk,
  onGetInitialValuesThunk,
  onGetQuoteByCodeThunk,
  onOpenQuoteSavedSatisfactoryDialog,
  onResetQuoteStepOneState,
  onSaveQuoteStepOneThunk,
  onSeconCallAutomatic,
  onOffSeconCallAutomatic
} from './QuoteStepOne.actions'
import { Divider, Grid, Root } from './QuoteStepOne.styles'
import { useHasReachedMaxDate } from '../../modules/hooks/maxDate'
import { inIframe } from '../../modules/utils/inIframe'

/*
 * The QuoteStepOne's container.
 */
const QuoteStepOneContainer = () => {
  const dispatch = useDispatch()
  const formRef = useFormRef()
  const navigate = useNavigate()
  const query = useQuery()
  const hasReachedMaxDate = useHasReachedMaxDate()
  const { regionInfo } = useConfig()
  const { authInfo } = useSelector(state => state.authorizationProvider)
  const {
    errors,
    initData,
    loading,
    openSaveDialog: openDraf,
    openSaveErrorDialog,
    quote,
    saving,
    secondCallAutomatic
  } = useSelector(state => state.quoteStepOne)
  const { size } = useViewport()
  const quoteCode = useQueryParam('quote')

  const handleChange = event => {
    if (event.target.checked) {
      dispatch(onSeconCallAutomatic())
    } else {
      dispatch(onOffSeconCallAutomatic())
    }
  }

  const isInIframe = useMemo(() => {
    return inIframe()
  }, [])

  const handleRegionChange = updatedValue => dispatch(onGetDistrictsThunk({ stateID: updatedValue }))
  const handleSaveQuoteDraft = () => {
    dispatch(
      onSaveQuoteStepOneThunk({
        address: formRef.current.addressValue,
        amount: formRef.current.amountValue,
        closingDate: formRef.current.closingDateValue,
        closingTime: formRef.current.closingTimeValue,
        code: quote.code,
        contactName: formRef.current.contactNameValue,
        contactPhone: formRef.current.contactPhoneValue,
        contactEmail: formRef.current.contactEmailValue,
        deliveryTime: formRef.current.deliveryTimeValue,
        detail: formRef.current.descriptionValue,
        districtCode: formRef.current.districtValue,
        exchangeRate: formRef.current.exchangeRateValue,
        name: formRef.current.nameValue,
        regionCode: formRef.current.regionValue,
        secondCallAutomatic,
        budgetType: formRef.current.valueBudgetTypeSelected,
        callback: () => dispatch(onOpenQuoteSavedSatisfactoryDialog())
      })
    )
  }
  const handleBack = () => {
    dispatch(onResetQuoteStepOneState())
    navigate(-1)
  }
  const handleSubmit = () => {
    dispatch(
      onSaveQuoteStepOneThunk({
        address: formRef.current.addressValue,
        amount: formRef.current.amountValue,
        closingDate: formRef.current.closingDateValue,
        closingTime: formRef.current.closingTimeValue,
        code: quote.code,
        contactName: formRef.current.contactNameValue,
        contactPhone: formRef.current.contactPhoneValue,
        contactEmail: formRef.current.contactEmailValue,
        deliveryTime: formRef.current.deliveryTimeValue,
        detail: formRef.current.descriptionValue,
        districtCode: formRef.current.districtValue,
        exchangeRate: formRef.current.exchangeRateValue,
        name: formRef.current.nameValue,
        regionCode: formRef.current.regionValue,
        secondCallAutomatic,
        budgetType: formRef.current.valueBudgetTypeSelected,
        callback: ({ quoteCode }) => {
          query.set('quote', quoteCode)
          navigate({ pathname: routes.quoteStepTwo, search: query.toString() }, { replace: true })
        }
      })
    )
  }

  const disableDraft = formRef.current.closingDateValue === null || formRef.current.closingDateError !== ''

  const disableNext =
    formRef.current.addressValue === '' ||
    formRef.current.addressError !== '' ||
    formRef.current.amountValue === '' ||
    formRef.current.amountError !== '' ||
    formRef.current.closingDateValue === null ||
    formRef.current.closingDateError !== '' ||
    formRef.current.contactEmailValue === '' ||
    formRef.current.contactEmailError !== '' ||
    formRef.current.contactNameValue === '' ||
    formRef.current.contactNameError !== '' ||
    formRef.current.contactPhoneValue === '' ||
    formRef.current.contactPhoneError !== '' ||
    formRef.current.deliveryTimeValue === '' ||
    formRef.current.deliveryTimeError !== '' ||
    formRef.current.descriptionValue === '' ||
    formRef.current.descriptionError !== '' ||
    formRef.current.districtValue === '' ||
    formRef.current.districtError !== '' ||
    formRef.current.exchangeRateValue === '' ||
    formRef.current.exchangeRateError !== '' ||
    formRef.current.nameValue === '' ||
    formRef.current.nameError !== '' ||
    formRef.current.regionValue === '' ||
    formRef.current.regionError !== ''

  useEffect(() => {
    if (quoteCode) {
      dispatch(onGetQuoteByCodeThunk({ code: quoteCode }))
    }
  }, [quoteCode])

  useEffect(() => {
    topPage(isInIframe, openSaveErrorDialog)
  }, [openSaveErrorDialog])

  useEffect(() => {
    topPage(isInIframe, openDraf)
  }, [openDraf])

  useEffectOnce(() => {
    dispatch(onResetQuoteStepOneState())
    dispatch(onGetInitialValuesThunk())
  })
  return (
    <Root>
      {!loading && initData.error && (
        <Grid container margin="16px 0 24px">
          <Grid item xs={12}>
            <Toast severity="error" title="Lo sentimos, estamos presentando problemas con nuestro servidor.">
              En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos
              minutos más.
            </Toast>
          </Grid>
        </Grid>
      )}
      <Divider />
      <BasicInformation
        description={quote?.description}
        formRef={formRef}
        isDownToTiny={size.isDownToTiny}
        name={quote?.name}
      />
      <Divider />
      <ClosingDate
        closeDate={quote?.closeDate}
        formRef={formRef}
        isDownToTiny={size.isDownToTiny}
        minDate={initData?.minDate}
        maxDate={initData?.maxDate}
      />
      <Divider />
      <SecondCallAutomaticComponent handleChange={handleChange} secondCallAutomatic={secondCallAutomatic} />
      <Divider />
      <DeliveryAddress
        delivery={quote?.delivery}
        districts={initData?.districts}
        formRef={formRef}
        isDownToTiny={size.isDownToTiny}
        onRegionChange={handleRegionChange}
        regionInfo={regionInfo}
      />
      <Divider />
      <DeliveryTime
        formRef={formRef}
        isDownToTiny={size.isDownToTiny}
        maxDays={initData?.maxDays}
        period={quote?.delivery?.period}
      />
      <Divider />
      <EstimatedBudget
        budget={quote?.estimatedBudget}
        exchangeRates={initData?.exchangeRates}
        formRef={formRef}
        isDownToTiny={size.isDownToTiny}
        maxAmountsUTMs={initData.maxAmountsUTMs}
        serverDate={initData?.serverDate}
      />
      <Grid container margin="64px 0 16px">
        <Grid item xs={12}>
          <Typography fontWeight="bold" variant="h3">
            Contacto para esta cotización
          </Typography>
        </Grid>
      </Grid>
      <Divider />
      <ContactInformation contact={quote?.contact} formRef={formRef} isDownToTiny={size.isDownToTiny} />
      <Errors errors={errors} openDialog={openSaveErrorDialog} maxUtm={initData.maxAmountsUTMs} />
      <Divider />
      <Grid container margin="32px 0">
        <Grid item xs={12} sm={2}>
          <Button onClick={handleBack} variant="outlined" width="100%">
            Volver
          </Button>
        </Grid>
        <Grid item xs={12} sm={10} padding={size.isDownToTiny ? '0 0 50px 0' : '0'}>
          <Grid container justifyContent="flex-end">
            <Button
              disabled={disableDraft || saving || authInfo.hasObserverRole}
              margin={size.isDownToTiny ? '10px 0 0' : '0 12px 0 0'}
              onClick={handleSaveQuoteDraft}
              variant="contained"
              width={size.isDownToTiny ? '100%' : '220px'}
            >
              Guardar Borrador
            </Button>
            {!hasReachedMaxDate && (
              <Button
                color="primary"
                disabled={disableNext || saving || loading}
                loading={loading}
                margin={size.isDownToTiny ? '10px 0 0' : '0'}
                variant="contained"
                width={size.isDownToTiny ? '100%' : '173px'}
                onClick={handleSubmit}
              >
                Siguiente
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Root>
  )
}

export default QuoteStepOneContainer
